import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { type Allowances } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import { parseNumber } from 'pages/employer_onboarding/utils'
import { Button, NumberMask, TextInput } from 'slab'

interface FlatDefaultAllowanceStepProps {
  onBack: () => void
}

export function FlatDefaultAllowanceStep ({ onBack }: FlatDefaultAllowanceStepProps) {
  const { control, register, formState: { errors } } = useFormContext<Allowances>()

  const { fields: allowanceFields } = useFieldArray({
    control,
    name: 'allowances',
  })

  const defaultAllowanceIndex = useMemo(() => {
    return allowanceFields.findIndex(allowance => !allowance.class)
  }, [allowanceFields])

  const loading = defaultAllowanceIndex === -1

  return (
    <div className='flex flex-col gap-4 p-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='heading-sm text-gray-800'>Default allowance</h2>
          <p className='body text-gray-500 mt-1'>
            These allowances will apply to all your employees.
          </p>
        </div>
        <Button
          variant='secondary'
          size='small'
          onClick={onBack}
        >
          Back
        </Button>
      </div>

      <div className='flex gap-4'>
        <div className='flex-1'>
          <NumberMask>
            <TextInput
              label="Employee's allowance"
              type='number'
              min={0}
              disabled={loading}
              {...register(`allowances.${defaultAllowanceIndex}.flat_config.employee_amount`, {
                required: 'Employee allowance is required',
                validate: (value) => {
                  if (value === undefined) return 'Employee allowance is required'
                  if (value <= 0) return 'Employee allowance must be greater than 0'
                  return true
                },
                setValueAs: (value) => parseNumber(value),
              })}
              error={errors.allowances?.[defaultAllowanceIndex]?.flat_config?.employee_amount?.message?.toString()}
            />
          </NumberMask>
        </div>
        <div className='flex-1'>
          <NumberMask>
            <TextInput
              label='First dependent allowance'
              type='number'
              min={0}
              disabled={loading}
              {...register(`allowances.${defaultAllowanceIndex}.flat_config.spouse_amount`, {
                required: 'First dependent allowance is required',
                validate: (value) => {
                  if (value === undefined) return 'First dependent allowance is required'
                  if (value < 0) return 'Must be greater than or equal to 0'
                  return true
                },
                setValueAs: (value) => parseNumber(value),
              })}
              error={errors.allowances?.[defaultAllowanceIndex]?.flat_config?.spouse_amount?.message?.toString()}
            />
          </NumberMask>
        </div>
        <div className='flex-1'>
          <NumberMask>
            <TextInput
              label="Additional dependents' allowance"
              type='number'
              min={0}
              disabled={loading}
              {...register(`allowances.${defaultAllowanceIndex}.flat_config.dependent_amount`, {
                required: 'Additional dependents allowance is required',
                validate: (value) => {
                  if (value === undefined) return 'Additional dependents allowance is required'
                  if (value < 0) return 'Must be greater than or equal to 0'
                  return true
                },
                setValueAs: (value) => parseNumber(value),
              })}
              error={errors.allowances?.[defaultAllowanceIndex]?.flat_config?.dependent_amount?.message?.toString()}
            />
          </NumberMask>
        </div>
      </div>
    </div>
  )
}
